/**
 * Rollback Confirmation Modal
 * 
 * Allows platform admins to rollback models to a previous snapshot state.
 * Requires a reason for audit trail and optionally allows selecting specific algorithms.
 */

import React, { useState } from 'react';
import { X, AlertTriangle, RotateCcw, CheckCircle } from 'lucide-react';
import { getApiBase, getJsonHeaders } from '../../../utils/apiHelpers';

interface RollbackConfirmationModalProps {
  snapshotId: string;
  tenantId: string;
  modelCount: number;
  availableAlgorithms?: string[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface RollbackResponse {
  snapshot_id: string;
  tenant_id: string;
  algorithms_restored: string[];
  models_restored: number;
  rollback_reason: string;
  rolled_back_at: string;
}

const RollbackConfirmationModal: React.FC<RollbackConfirmationModalProps> = ({
  snapshotId,
  tenantId,
  modelCount,
  availableAlgorithms = [],
  isOpen,
  onClose,
  onSuccess
}) => {
  const [reason, setReason] = useState('');
  const [selectedAlgorithms, setSelectedAlgorithms] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [rollbackResult, setRollbackResult] = useState<RollbackResponse | null>(null);

  const handleAlgorithmToggle = (algorithm: string) => {
    setSelectedAlgorithms(prev => 
      prev.includes(algorithm)
        ? prev.filter(a => a !== algorithm)
        : [...prev, algorithm]
    );
  };

  const handleRollback = async () => {
    if (reason.trim().length < 10) {
      setError('Reason must be at least 10 characters');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const url = `${getApiBase()}/api/platform-admin/ml/snapshots/${tenantId}/${snapshotId}/rollback`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getJsonHeaders(),
        body: JSON.stringify({
          reason: reason.trim(),
          algorithms: selectedAlgorithms.length > 0 ? selectedAlgorithms : null
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Rollback failed: ${response.statusText}`);
      }

      const result: RollbackResponse = await response.json();
      setRollbackResult(result);
      setSuccess(true);

      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 2000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rollback snapshot');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setSelectedAlgorithms([]);
    setError(null);
    setSuccess(false);
    setRollbackResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <RotateCcw className="w-6 h-6 text-orange-600" />
            <h2 className="text-xl font-bold text-gray-900">Rollback Confirmation</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {success && rollbackResult ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-green-900 mb-2">Rollback Successful!</h3>
              <p className="text-green-700 mb-4">
                Successfully restored {rollbackResult.models_restored} model{rollbackResult.models_restored !== 1 ? 's' : ''}
              </p>
              <div className="text-sm text-green-600">
                <p>Algorithms restored: {rollbackResult.algorithms_restored.join(', ')}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Warning */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-orange-900">Warning: Model Rollback</h3>
                  <p className="text-orange-700 text-sm mt-1">
                    This action will restore {modelCount} model{modelCount !== 1 ? 's' : ''} from snapshot <span className="font-mono">{snapshotId.substring(0, 8)}...</span>
                  </p>
                  <p className="text-orange-700 text-sm mt-1">
                    Currently deployed models will be stopped and replaced with snapshot versions.
                  </p>
                </div>
              </div>

              {/* Snapshot Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Snapshot ID:</span>
                  <span className="text-sm font-mono text-gray-900">{snapshotId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Models to restore:</span>
                  <span className="text-sm font-semibold text-gray-900">{modelCount}</span>
                </div>
              </div>

              {/* Algorithm Selection (Optional) */}
              {availableAlgorithms.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Algorithms (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Leave empty to rollback all models, or select specific algorithms to rollback
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {availableAlgorithms.map(algorithm => (
                      <label
                        key={algorithm}
                        className="flex items-center gap-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedAlgorithms.includes(algorithm)}
                          onChange={() => handleAlgorithmToggle(algorithm)}
                          disabled={isSubmitting}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-900">{algorithm}</span>
                      </label>
                    ))}
                  </div>
                  {selectedAlgorithms.length > 0 && (
                    <p className="text-xs text-blue-600 mt-2">
                      {selectedAlgorithms.length} algorithm{selectedAlgorithms.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              )}

              {/* Reason Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rollback Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={isSubmitting}
                  placeholder="Enter reason for rollback (minimum 10 characters)..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <div className="flex justify-between mt-1">
                  <p className="text-xs text-gray-500">
                    This will be recorded in the audit trail
                  </p>
                  <p className={`text-xs ${reason.length >= 10 ? 'text-green-600' : 'text-gray-400'}`}>
                    {reason.length}/10 characters
                  </p>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleRollback}
              disabled={isSubmitting || reason.trim().length < 10}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Rolling back...
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4" />
                  Confirm Rollback
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RollbackConfirmationModal;
